import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  MouseSensor,
  type SensorDescriptor,
  type SensorOptions,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { TourClasses } from '@revelio/core';

import { type TabItem, useTabOrder } from '../hooks/useTabOrder';
import type { AnalyticsEntity } from '../utils/revelio-primary-filter-entity-switch';
import styles from './analytics-view-tabs.module.css';
import { DraggableTabLink } from './DraggableTabLink';
import { PagesMenu } from './PagesMenu';
import {
  PAGES_MENU_SURFACE_ID,
  PAGES_MENU_TRIGGER_ID,
  TAB_BAR_LIST_ID,
  tabDragCollisionDetection,
} from './tab-drag-collision';
import {
  formatTransform,
  INDICATOR_BASE_WIDTH,
  measureLabelRect,
} from './tab-indicator';

const GRABBING_STYLE_ID = 'tab-bar-grabbing-cursor';

// dnd-kit falls back to its default sensors only when `sensors` is undefined.
const NO_SENSORS: SensorDescriptor<SensorOptions>[] = [];

/**
 * dnd-kit swallows the click that ends a drag by stopping its propagation at
 * the document, so it never reaches React. That does not cancel the default
 * action of the link the drag started on: the browser follows the dragged
 * tab's href with a full page load, abandoning the save of the new order on
 * the way out. The window sees the click before the document does, so the
 * default is cancelled there.
 */
const cancelDropClick = () => {
  const cancel = (event: MouseEvent) => event.preventDefault();
  window.addEventListener('click', cancel, { capture: true, once: true });
  // The drop may not produce a click at all (the browser skips it when the
  // pressed element moved), so don't leave the listener for a later one.
  setTimeout(
    () => window.removeEventListener('click', cancel, { capture: true }),
    100
  );
};

export const AnalyticsViewTabs = ({ entity }: { entity: AnalyticsEntity }) => {
  const location = useLocation();
  const tabRefs = useRef<Map<string, HTMLAnchorElement | null>>(new Map());
  const indicatorRef = useRef<HTMLDivElement>(null);

  const {
    orderedTabs,
    barTabs,
    menuTabs,
    previewMove,
    saveOrder,
    commitDropOnMenu,
    cancelPreview,
    canReorder,
  } = useTabOrder(entity);
  const [activeTab, setActiveTab] = useState<TabItem | null>(null);

  const isDragging = activeTab !== null;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } })
  );

  const activeBarTab = barTabs.find((t) => t.path === location.pathname);
  const activeMenuTab = menuTabs.find((t) => t.path === location.pathname);
  // Tab object identity is stable across reorders (same ref, new slot), so the
  // indicator effect needs the position as a separate dep to re-slide.
  const activeBarIdx = activeBarTab
    ? barTabs.findIndex((t) => t.id === activeBarTab.id)
    : -1;

  // The indicator underlines only the label text, not the full hit area.
  const moveIndicatorTo = useCallback(
    (tab: HTMLAnchorElement | null | undefined) => {
      const el = indicatorRef.current;
      if (!tab || !el) return;
      el.style.opacity = '1';
      el.style.transform = formatTransform(measureLabelRect(tab));
    },
    []
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeBarIdx is a re-run trigger — activeBarTab's object identity is stable across reorders, so position changes need this extra signal to re-position.
  useLayoutEffect(() => {
    const el = indicatorRef.current;
    if (!el) return;
    // During drag, sortable transforms move tabs but offsetLeft stays at the
    // pre-drag position, so any move would point at the wrong slot. Hide the
    // indicator until the drag commits and the bar re-renders in final order.
    if (isDragging || !activeBarTab) {
      el.style.opacity = '0';
      return;
    }
    // Positioned synchronously before paint: the underline jumps straight to
    // the active tab with no slide.
    moveIndicatorTo(tabRefs.current.get(activeBarTab.id) ?? null);
  }, [activeBarTab, activeBarIdx, moveIndicatorTo, isDragging]);

  const handleDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      if (!over || active.id === over.id) return;
      previewMove(String(active.id), String(over.id));
    },
    [previewMove]
  );

  const setGrabbingCursor = useCallback(() => {
    if (document.getElementById(GRABBING_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = GRABBING_STYLE_ID;
    // Universal selector + !important is the only way to beat per-element
    // cursors (anchors, buttons, inputs) during a drag.
    style.textContent = `*, *::before, *::after { cursor: grabbing !important; }`;
    document.head.appendChild(style);
  }, []);

  const clearGrabbingCursor = useCallback(() => {
    document.getElementById(GRABBING_STYLE_ID)?.remove();
  }, []);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const id = String(active.id);
      const tab = orderedTabs.find((t) => t.id === id) ?? null;
      setGrabbingCursor();
      setActiveTab(tab);
    },
    [setGrabbingCursor, orderedTabs]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      clearGrabbingCursor();
      setActiveTab(null);
      cancelDropClick();
      if (
        over?.id === PAGES_MENU_TRIGGER_ID ||
        over?.id === PAGES_MENU_SURFACE_ID
      ) {
        commitDropOnMenu(String(active.id));
        return;
      }
      saveOrder();
    },
    [clearGrabbingCursor, saveOrder, commitDropOnMenu]
  );

  const handleDragCancel = useCallback(() => {
    clearGrabbingCursor();
    setActiveTab(null);
    cancelPreview();
  }, [clearGrabbingCursor, cancelPreview]);

  return (
    <DndContext
      sensors={canReorder ? sensors : NO_SENSORS}
      collisionDetection={tabDragCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <nav
        aria-label="Analytics views"
        className={classNames(
          TourClasses.TOUR_ANALYTICS_VIEW_TABS_CLASS,
          styles.tabs
        )}
        data-testid="analytics-view-tabs"
      >
        <SortableContext
          id={TAB_BAR_LIST_ID}
          items={barTabs.map((t) => t.id)}
          strategy={horizontalListSortingStrategy}
        >
          {barTabs.map((tab) => (
            <DraggableTabLink
              key={tab.id}
              tab={tab}
              variant="bar"
              isActive={activeBarTab?.id === tab.id}
              tabRef={(el) => tabRefs.current.set(tab.id, el)}
              onNavigate={() =>
                moveIndicatorTo(tabRefs.current.get(tab.id) ?? null)
              }
              testId={`analytics-view-tab-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
            />
          ))}
        </SortableContext>
        <div
          ref={indicatorRef}
          aria-hidden
          className={styles.tabIndicator}
          style={{
            width: `${INDICATOR_BASE_WIDTH}px`,
            transform: 'translate3d(0, 0, 0) scaleX(0)',
            opacity: 0,
          }}
        />
        <PagesMenu
          entity={entity}
          menuTabs={menuTabs}
          activeMenuTabId={activeMenuTab?.id ?? null}
        />
      </nav>
      <DragOverlay dropAnimation={null}>
        {activeTab ? <TabGhost label={activeTab.label} /> : null}
      </DragOverlay>
    </DndContext>
  );
};

const TabGhost = ({ label }: { label: string }) => (
  <span className={styles.tabGhost}>{label}</span>
);
